import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Frame } from "lucide-react";
import { axiosInstance } from "../../config/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import FramedPhoto from "./FramedPhoto";
import { refreshActiveFrames } from "./useActiveFrames";

const Sample = ({ frame, label }) => (
  <div className="flex flex-col items-center gap-3 pt-5 pb-3">
    <FramedPhoto frame={frame} size={72} fallback={<div className="w-full h-full rounded-full bg-gradient-to-br from-sky-200 to-pink-200 grid place-items-center text-2xl font-black text-white">A</div>} />
    <span className="text-[11px] font-bold text-slate-500 mt-2">{label}</span>
  </div>
);

/** Tetapan bingkai bermusim (Superadmin). */
export default function FrameSettingsDialog({ open, onOpenChange }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axiosInstance.get("/photo-frames/settings").then((r) => setS(r.data)).catch(() => toast.error("Gagal memuat tetapan bingkai"));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.put("/photo-frames/settings", {
        season_key: s.season_key || "", start_date: s.start_date || "", end_date: s.end_date || "",
        birthday_enabled: !!s.birthday_enabled, show_on_cards: !!s.show_on_cards,
      });
      setS((x) => ({ ...x, ...data }));
      refreshActiveFrames();
      toast.success(data.active_season ? `Bingkai ${data.active_season.label} kini aktif` : "Tetapan bingkai disimpan");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="frame-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Frame className="w-5 h-5 text-pink-500" /> Bingkai Gambar Bermusim</DialogTitle>
          <DialogDescription>Bingkai dipaparkan pada gambar profil kanak-kanak dan ibu bapa boleh berkongsi gambar berbingkai.</DialogDescription>
        </DialogHeader>
        {!s ? (
          <div className="grid place-items-center h-32"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 rounded-xl bg-slate-50 px-2">
              <Sample frame="birthday" label="Hari Jadi" />
              {s.seasons.map((x) => <Sample key={x.key} frame={x.key} label={x.label.replace("Hari Raya ", "Raya ").replace(" & Malaysia", "")} />)}
            </div>
            <div className="space-y-1.5">
              <Label>Bingkai musim aktif</Label>
              <Select value={s.season_key || "none"} onValueChange={(v) => setS({ ...s, season_key: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="frame-season-select"><SelectValue /></SelectTrigger>
                <SelectContent className="z-[10002]">
                  <SelectItem value="none">Tiada (matikan bingkai musim)</SelectItem>
                  {s.seasons.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Tarikh mula</Label><Input type="date" value={s.start_date || ""} onChange={(e) => setS({ ...s, start_date: e.target.value })} data-testid="frame-start-date" /></div>
              <div className="space-y-1.5"><Label>Tarikh tamat</Label><Input type="date" value={s.end_date || ""} onChange={(e) => setS({ ...s, end_date: e.target.value })} data-testid="frame-end-date" /></div>
            </div>
            <p className="text-[11px] text-slate-500 -mt-2">Kosongkan tarikh untuk aktif serta-merta tanpa had. Hari ini: {s.today}</p>
            <div className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
              <div><div className="text-sm font-bold text-slate-700">Bingkai Hari Jadi automatik</div><div className="text-[11px] text-slate-500">Aktif selama seminggu bermula hari jadi kanak-kanak</div></div>
              <Switch checked={!!s.birthday_enabled} onCheckedChange={(v) => setS({ ...s, birthday_enabled: v })} data-testid="frame-birthday-switch" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
              <div><div className="text-sm font-bold text-slate-700">Papar bingkai pada kad kanak-kanak</div><div className="text-[11px] text-slate-500">Jika dimatikan, bingkai hanya ada semasa ibu bapa berkongsi</div></div>
              <Switch checked={!!s.show_on_cards} onCheckedChange={(v) => setS({ ...s, show_on_cards: v })} data-testid="frame-cards-switch" />
            </div>
            <Button onClick={save} disabled={saving} className="w-full bg-pink-500 hover:bg-pink-600 text-white" data-testid="frame-settings-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan Tetapan
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
